// Implements: F-UIX-010, F-UIX-018
/**
 * A `429` to a paid post is a wait, not a failure (UIX.2).
 *
 * The wait is the `Retry-After` the server sent (delta-seconds, `parseRetryAfter`), capped at
 * `MAX_STREAM_WAIT_S` like the run stream: the backend's per-minute limit never asks more, and
 * a larger value is a broken header. The hook counts it down one second at a time and returns
 * the seconds left, or `null` when there is nothing to wait for. It never sends anything: the
 * post stays `retry: false` (checklist 13), and the person presses again when the wait ends.
 *
 * The count belongs to the error object it was started for, so a new `429` starts a new wait
 * and an old one never finishes a newer one. The ticker is injectable so tests move time by
 * hand (checklist 16).
 */

import { useEffect, useState } from "react";

import type { ApiError } from "./ApiError";
import { MAX_STREAM_WAIT_S } from "./followRun";
import { parseRetryAfter } from "./rateLimit";

/** Calls `tick` every `ms` until the returned function is called. */
export interface Ticker {
  readonly every: (ms: number, tick: () => void) => () => void;
}

export const BROWSER_TICKER: Ticker = {
  every: (ms, tick) => {
    const id = setInterval(tick, ms);
    return () => {
      clearInterval(id);
    };
  },
};

const SECOND_MS = 1000;

/** The seconds to wait after `error`, or `null` when it is not a rate limit. */
export function rateLimitWaitSeconds(error: ApiError): number | null {
  if (error.status !== 429) return null;
  return Math.min(parseRetryAfter(error.headers.get("Retry-After")), MAX_STREAM_WAIT_S);
}

export function useRateLimitWait(error: ApiError | null, ticker: Ticker = BROWSER_TICKER): number | null {
  const [elapsed, setElapsed] = useState<{ readonly error: ApiError | null; readonly seconds: number }>({
    error: null,
    seconds: 0,
  });
  const total = error === null ? null : rateLimitWaitSeconds(error);

  useEffect(() => {
    if (total === null) return undefined;
    let seconds = 0;
    const stop = ticker.every(SECOND_MS, () => {
      seconds += 1;
      setElapsed({ error, seconds });
      if (seconds >= total) stop();
    });
    return stop;
  }, [error, total, ticker]);

  if (total === null) return null;
  const left = total - (elapsed.error === error ? elapsed.seconds : 0);
  return left > 0 ? left : null;
}
